import React, { useState } from 'react';
import Modal from 'src/shared/components/modal/Modal';
import Input from 'src/shared/components/input/Input';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import ScopeCheckboxList from './ScopeCheckboxList';
import type { CreateApiTokenRequest, ScopeDescriptor } from '../../state/api/engineApiTokensEngineSlice';

type CreateTokenModalProps = {
    scopes: ScopeDescriptor[];
    isLoadingScopes: boolean;
    onSubmit: (req: CreateApiTokenRequest) => Promise<void>;
    onClose: () => void;
};

const DUPLICATE_NAME_MESSAGE = 'A token with that name already exists.';
const GENERIC_ERROR_MESSAGE = 'Unable to create token — please try again.';

type SubmissionError = {
    status?: number | string;
    data?: { error?: string };
};

const extractErrorMessage = (error: SubmissionError): string => {
    if (error.status === 409) return DUPLICATE_NAME_MESSAGE;
    return error.data?.error ?? GENERIC_ERROR_MESSAGE;
};

const CreateTokenModal = ({ scopes, isLoadingScopes, onSubmit, onClose }: CreateTokenModalProps) => {
    const [name, setName] = useState('');
    const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const trimmedName = name.trim();
    const canSubmit = trimmedName.length > 0 && selectedScopes.size > 0 && !isSubmitting;

    const toggleScope = (scope: string) => {
        setSelectedScopes(prev => {
            const next = new Set(prev);
            if (next.has(scope)) next.delete(scope);
            else next.add(scope);
            return next;
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            await onSubmit({
                name: trimmedName,
                allowed_scopes: Array.from(selectedScopes),
            });
        } catch (err) {
            setErrorMessage(extractErrorMessage(err as SubmissionError));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal onClose={onClose} dataTestId='create-token-modal'>
            <div className='flex flex-col gap-6 p-2 min-w-[28rem] max-w-2xl'>
                <header className='flex flex-col gap-1'>
                    <h2 className='text-xl font-semibold text-primary'>New Engine API token</h2>
                    <p className='text-sm text-grey-70'>The token is shown once after creation. Save it somewhere secure.</p>
                </header>

                <div className='flex flex-col gap-2'>
                    <label htmlFor='engine-api-token-name' className='text-sm font-medium text-primary'>
                        Name
                    </label>
                    <Input
                        id='engine-api-token-name'
                        value={name}
                        onChange={event => setName(event.target.value)}
                        placeholder='e.g. dagster-staging'
                        disabled={isSubmitting}
                    />
                </div>

                <div className='flex flex-col gap-2'>
                    <span className='text-sm font-medium text-primary'>Scopes</span>
                    {isLoadingScopes && scopes.length === 0 ? (
                        <span className='text-sm text-grey-70'>Loading scopes…</span>
                    ) : (
                        <ScopeCheckboxList scopes={scopes} selected={selectedScopes} onToggle={toggleScope} />
                    )}
                </div>

                {errorMessage && (
                    <div className='text-sm text-red-100' role='alert'>
                        {errorMessage}
                    </div>
                )}

                <div className='flex justify-end gap-3'>
                    <SecondaryButton onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>
                        Create
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default CreateTokenModal;
